import { Validator } from './validator';

export class DBValidationError extends Error {
    constructor(public readonly errors: string) {
        super(errors);
        this.name = 'DBValidationError';
    }
}

export type Logger = {
    error(message: string): void;
};

const UNKNOWN_VALIDATION_ERROR = 'Unknown validation error';

export interface DatabaseInputValidation<
    Input extends Record<string, unknown>,
    Output = Input,
> {
    execute(input: Input): Output;
}

export class DBInputValidation<
    Input extends Record<string, unknown>,
    Output = Input,
> implements DatabaseInputValidation<Input, Output>
{
    constructor(private readonly validator: Validator<Input, Output>) {}

    execute(input: Input): Output {
        const result = this.validator.validate(input);

        if (result.success === false) {
            throw new DBValidationError(
                result.errors?.join(', ') ?? UNKNOWN_VALIDATION_ERROR,
            );
        }

        return result.data;
    }
}

export class DBInputSoftValidation<
    Input extends Record<string, unknown>,
    Output = Input,
> implements DatabaseInputValidation<Input, Output>
{
    constructor(
        private readonly validator: Validator<Input, Output>,
        private readonly logger: Logger,
        private readonly hardErrorFields: Array<keyof Input & string> = [],
    ) {}

    execute(input: Input): Output {
        const result = this.validator.validate(input);

        if (result.success === false) {
            if (
                this.hardErrorFields?.some((field) =>
                    result.errorFields?.includes(field),
                )
            ) {
                throw new DBValidationError(
                    result.errors?.join(', ') ?? UNKNOWN_VALIDATION_ERROR,
                );
            }

            this.logger.error(
                result.errors?.join(', ') ?? UNKNOWN_VALIDATION_ERROR,
            );
            return input as unknown as Output;
        }

        return result.data;
    }
}
